/* ===========================================
   Custom Announcement Bar — Wrapper Element
   =========================================== */
class AnnouncementBarCustom extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    if (sessionStorage.getItem('announce-dismissed-' + this.sectionId)) {
      this.classList.add('announcement-bar-custom--hidden');
      document.documentElement.style.setProperty('--announcement-height', '0px');
      return;
    }

    const dismissBtn = this.querySelector('.announcement-bar-custom__dismiss');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', this.dismiss.bind(this));
    }
  }

  dismiss() {
    sessionStorage.setItem('announce-dismissed-' + this.sectionId, '1');
    this.classList.add('announcement-bar-custom--hidden');
    document.documentElement.style.setProperty('--announcement-height', '0px');
  }
}

if (!customElements.get('announcement-bar-custom')) {
  customElements.define('announcement-bar-custom', AnnouncementBarCustom);
}

/* ===========================================
   Custom Announcement Bar — Countdown Element
   =========================================== */
class AnnouncementBarCountdown extends HTMLElement {
  constructor() {
    super();
    this.interval = null;
    this.targetTime = null;
  }

  connectedCallback() {
    this.mode = this.dataset.mode;
    this.separator = this.dataset.separator;
    this.onExpire = this.dataset.onExpire;
    this.expiredMessage = this.dataset.expiredMessage || 'Sale ended';
    this.sectionId = this.dataset.sectionId;
    this.showDays = this.dataset.showDays !== 'false';
    this.showHours = this.dataset.showHours !== 'false';
    this.showMinutes = this.dataset.showMinutes !== 'false';
    this.showSeconds = this.dataset.showSeconds !== 'false';
    this.labelStyle = this.dataset.labelStyle || 'short';

    if (this.mode === 'end_date') {
      this.targetTime = this.parseDate(this.dataset.endDate);
    } else if (this.mode === 'duration') {
      var storageKey = 'announce-cd-end-' + this.sectionId;
      var stored = sessionStorage.getItem(storageKey);
      if (stored) {
        this.targetTime = parseInt(stored, 10);
      } else {
        var d = parseInt(this.dataset.durationDays, 10) || 0;
        var h = parseInt(this.dataset.durationHours, 10) || 0;
        var m = parseInt(this.dataset.durationMinutes, 10) || 0;
        this.targetTime = Date.now() + ((d * 86400 + h * 3600 + m * 60) * 1000);
        sessionStorage.setItem(storageKey, this.targetTime.toString());
      }
    }

    if (!this.targetTime || isNaN(this.targetTime)) return;

    this.tick();
    this.interval = setInterval(this.tick.bind(this), 1000);
  }

  parseDate(str) {
    if (!str) return null;
    var ts = Date.parse(str);
    if (isNaN(ts)) {
      // Safari fallback: replace dashes with slashes
      ts = Date.parse(str.replace(/-/g, '/'));
    }
    return isNaN(ts) ? null : ts;
  }

  tick() {
    var diff = this.targetTime - Date.now();

    if (diff <= 0) {
      this.handleExpire();
      return;
    }

    var days = Math.floor(diff / 86400000);
    var hours = Math.floor((diff % 86400000) / 3600000);
    var mins = Math.floor((diff % 3600000) / 60000);
    var secs = Math.floor((diff % 60000) / 1000);

    this.render(days, hours, mins, secs);
  }

  getLabel(singular, plural, shortLabel, count) {
    if (this.labelStyle === 'none') return '';
    if (this.labelStyle === 'short') return shortLabel;
    return count === 1 ? singular : plural;
  }

  render(days, hours, mins, secs) {
    var sep = this.getSeparatorHTML();
    var pad = function(n) { return n < 10 ? '0' + n : '' + n; };

    var groups = [];

    if (this.showDays) {
      groups.push(this.buildGroup(days, this.getLabel('Day', 'Days', 'D', days)));
    }
    if (this.showHours) {
      groups.push(this.buildGroup(pad(hours), this.getLabel('Hour', 'Hours', 'H', hours)));
    }
    if (this.showMinutes) {
      groups.push(this.buildGroup(pad(mins), this.getLabel('Min', 'Mins', 'M', mins)));
    }
    if (this.showSeconds) {
      groups.push(this.buildGroup(pad(secs), this.getLabel('Sec', 'Secs', 'S', secs)));
    }

    this.innerHTML = groups.join(sep);
  }

  buildGroup(value, label) {
    var labelHTML = label ? '<span class="announcement-bar-custom__cd-label">' + label + '</span>' : '';
    return '<div class="announcement-bar-custom__cd-group">' +
      '<span class="announcement-bar-custom__cd-number">' + value + '</span>' +
      labelHTML +
    '</div>';
  }

  getSeparatorHTML() {
    if (this.separator === 'colon') {
      return '<span class="announcement-bar-custom__cd-separator">:</span>';
    }
    if (this.separator === 'dash') {
      return '<span class="announcement-bar-custom__cd-separator">&ndash;</span>';
    }
    return '';
  }

  handleExpire() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    switch (this.onExpire) {
      case 'hide_bar':
        var section = this.closest('.announcement-bar-custom');
        if (section) {
          section.classList.add('announcement-bar-custom--hidden');
          document.documentElement.style.setProperty('--announcement-height', '0px');
        }
        break;

      case 'show_message':
        this.innerHTML = '<span class="announcement-bar-custom__cd-expired">' +
          this.expiredMessage + '</span>';
        break;

      case 'hide_countdown':
      default:
        this.style.display = 'none';
        break;
    }
  }

  disconnectedCallback() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}

if (!customElements.get('announcement-bar-countdown')) {
  customElements.define('announcement-bar-countdown', AnnouncementBarCountdown);
}
